import {
    ApplicationFilters,
    ModuleScore,
} from "stores/ApplicationFilters/ApplicationFiltersStore";
import { ApplicationFilterValuesStore } from "stores/ApplicationFilters/ApplicationFilterValuesStore";
import { CallDirection } from "stores/ApplicationFilters/Filters/CallDirection";
import { DateReferenceOption } from "stores/ComponentStores/DatePickerComponentStore";
import RootStore from "stores/RootStore";
import { useStore } from "utils/useStore";
import AgentStore from "./Stores/AgentStore";
import { useNavigate } from "react-router";
import {
    SafetyEventAck,
    SafetyEventId,
    SafetyEventType,
    SafetyEventTypeOperator,
} from "stores/ApplicationFilters/Filters/SafetyEvent";
import { Operator } from "models/Rule";
import { Sentiment } from "stores/ApplicationFilters/Filters/Sentiment";
import { HipaaCompliance } from "stores/ApplicationFilters/Filters/HipaaCompliance";
import { PatientJourneyStage } from "stores/ApplicationFilters/Filters/PatientJourneyStage";
import { IOutlineFilter } from "components/Conversations/Views/Drawer/components/DeepFilter/OutlineFilterStore";

const emptyGUID = "00000000-0000-0000-0000-000000000000";

export interface KboModuleScoreFilter {
    moduleId?: string;
    moduleName?: string;
    operator: string;
    score: number;
    upperScore?: number;
}

export interface KboModelResultsFilter {
    modelId?: string;
    modelName?: string;
    modelResult?: string[];
}

type SafetyEventNavigationFilter = {
    identification?: SafetyEventId | keyof typeof SafetyEventId | null;
    typeOperator?: SafetyEventTypeOperator | keyof typeof SafetyEventTypeOperator;
    types?: (SafetyEventType | keyof typeof SafetyEventType)[];
    acknowledgments?: (SafetyEventAck | keyof typeof SafetyEventAck)[];
};

export interface AvaFilters {
    // AIAgentConversationFilters properties
    startDate?: string;
    endDate?: string;
    dateReference?: string;
    minCallDuration?: number;
    maxCallDuration?: number;
    hierarchyIds?: string[];
    hierarchyNames?: string[];
    themeIds?: string[];
    themeNames?: string[];
    wordsAndPhrasesSearchString?: string;
    classifierNames?: string[];
    classifierIds?: string[];
    classifierId?: string;
    agentName?: string;
    agentNames?: string[];
    agentIds?: string[];
    agentId?: string;
    contactTypes?: string[];
    patientJourneyStages?: PatientJourneyStage[];
    topicNames?: string[];
    driverNames?: string[];
    modelName?: string;
    modelResult?: string;
    models?: KboModelResultsFilter[];
    moduleScores?: KboModuleScoreFilter[];

    // AIAgentConversationGeneralFilters properties
    beginningSentiment?: Sentiment;
    endingSentiment?: Sentiment;
    eddyEffectStatus?: boolean;
    hipaaCompliance?: HipaaCompliance;
    safetyEventIdentification?: SafetyEventId;
    safetyEventTypeOperator?: SafetyEventTypeOperator;
    safetyEventTypes?: SafetyEventType[];
    safetyEventAcknowledgments?: SafetyEventAck[];
    safetyEvent?: SafetyEventNavigationFilter;
    adverseEvent?: boolean | null;
    callDirections?: CallDirection[];
    outlineSegmentNames?: string[][];
    excludeOutlineSegmentNames?: string[][];
}

export type NavigationActionData = AvaFilters | { conversationIds: string[] };

export function hasNavigationConversationIds(
    data: NavigationActionData,
): data is { conversationIds: string[] } {
    return "conversationIds" in data &&
        Array.isArray(data.conversationIds) &&
        data.conversationIds.length > 0;
}

export async function kboFiltersToApplicationFilters(
    filters: AvaFilters,
): Promise<ApplicationFilters> {
    const result: ApplicationFilters = {
        startDate: filters.startDate,
        endDate: filters.endDate,
        dateReferenceOption: getDateReferenceOption(filters.dateReference),
        minCallDuration: filters.minCallDuration,
        maxCallDuration: filters.maxCallDuration,
        evaluationTypes: [],
        hierarchyIds: filters.hierarchyIds ?? [],
        agentIds: filters.agentIds ?? [],
        mediaTypes: [],
        callDirections: filters.callDirections ?? [],
        beginningSentiment: filters.beginningSentiment,
        endingSentiment: filters.endingSentiment,
        eddyEffectStatus: filters.eddyEffectStatus,
        hipaaCompliance: filters.hipaaCompliance,
        topics: [],
        themes: [],
        primaryTopics: [],
        drivers: [],
        containsTopics: true,
        containsThemes: true,
        containsPrimaryTopics: true,
        containsDrivers: true,
        contactTypes: filters.contactTypes,
        patientJourneyStages: filters.patientJourneyStages,
    };

    if (filters.themeIds?.length || filters.themeNames?.length) {
        const valuesStore = RootStore().getStore(ApplicationFilterValuesStore);
        await valuesStore.loadDriverThemes();
        if (valuesStore.driverThemes) {
            result.themes = valuesStore.driverThemes.filter((theme) =>
                filters.themeIds?.includes(theme.id) ||
                filters.themeNames?.some(
                    (themeName) =>
                        themeName.localeCompare(theme.themeLabel, undefined, {
                            sensitivity: "accent",
                        }) === 0,
                ),
            );
        }
    }

    if (
        filters.topicNames?.length &&
        !filters.themeIds?.length &&
        !filters.themeNames?.length
    ) {
        const valuesStore = RootStore().getStore(ApplicationFilterValuesStore);
        await valuesStore.loadPrimaryTopics();
        if (valuesStore.primaryTopics) {
            result.primaryTopics = valuesStore.primaryTopics.filter((topic) =>
                filters.topicNames?.some(
                    (topicName) =>
                        topicName.localeCompare(topic.topicLabel, undefined, {
                            sensitivity: "accent",
                        }) === 0,
                ),
            );
        }
    }

    if (filters.driverNames?.length) {
        const valuesStore = RootStore().getStore(ApplicationFilterValuesStore);
        await valuesStore.loadPrimaryDrivers();
        if (valuesStore.primaryDrivers) {
            result.drivers = valuesStore.primaryDrivers.filter((driver) =>
                filters.driverNames?.some(
                    (driverName) =>
                        driverName.localeCompare(driver.driverName, undefined, {
                            sensitivity: "accent",
                        }) === 0,
                ),
            );
        }
    }

    const classifierIds = Array.from(
        new Set([
            ...(filters.classifierIds ?? []),
            ...(filters.classifierId ? [filters.classifierId] : []),
        ]),
    );

    if (classifierIds.length > 0) {
        result.rbcFilterItems = [
            {
                value: true,
                classifierIds,
                combinator: 0,
                filterGroupId: 0,
            },
        ];
    }
    
    if (filters.agentId && !result.agentIds?.includes(filters.agentId)) {
        result.agentIds = [...(result.agentIds ?? []), filters.agentId];
    }

    if (filters.agentNames?.length) {
        const valuesStore = RootStore().getStore(ApplicationFilterValuesStore);
        await valuesStore.loadAgents();
        if (valuesStore.agents.length > 0) {
            const matchedIds = filters.agentNames
                .map((name) => {
                    const trimmed = name.trim().toLowerCase();
                    return valuesStore.agents.find(
                        (agent) =>
                            agent.fullName?.trim().toLowerCase() === trimmed,
                    )?.id;
                })
                .filter((id): id is string => !!id);

            if (matchedIds.length > 0) {
                result.agentIds = Array.from(
                    new Set([...(result.agentIds ?? []), ...matchedIds]),
                );
            }
        }
    }

    if (filters.models?.length) {
        const valuesStore = RootStore().getStore(ApplicationFilterValuesStore);
        if (!valuesStore.llmClassifiers) {
            await valuesStore.loadLLMClassifiers();
        }
        const llmClassifiers = valuesStore.llmClassifiers ?? [];
        const resolved = filters.models
            .map((model) => {
                const matched = model.modelId
                    ? llmClassifiers.find((c) => c.id === model.modelId)
                    : model.modelName
                      ? llmClassifiers.find(
                            (c) =>
                                c.name.toLowerCase() ===
                                model.modelName?.toLowerCase(),
                        )
                      : undefined;

                if (!matched) return undefined;

                const requestedResults = model.modelResult ?? [];
                const normalizedResults =
                    requestedResults.length > 0
                        ? requestedResults.map(
                              (r) =>
                                  matched.results.find(
                                      (item) =>
                                          item.toLowerCase() === r.toLowerCase(),
                                  ) ?? r,
                          )
                        : matched.results;

                return {
                    id: matched.id,
                    name: matched.name,
                    results: normalizedResults,
                };
            })
            .filter((m): m is NonNullable<typeof m> => !!m);

        if (resolved.length > 0) {
            result.modelResults = resolved;
        }
    }

    if (filters.wordsAndPhrasesSearchString) {
        result.wordsAndPhrasesIsInputStandard = true;
        result.wordsAndPhrasesSearchString = filters.wordsAndPhrasesSearchString;
        result.wordsAndPhrasesItems = [
            {
                value: "Contains",
                searchPhrase: filters.wordsAndPhrasesSearchString,
                combinator: 0,
                filterGroupId: 0,
            },
        ];
    }

    const nestedSafetyEvent = filters.safetyEvent;
    const identificationInput = nestedSafetyEvent?.identification !== undefined
        ? nestedSafetyEvent.identification
        : filters.safetyEventIdentification !== undefined
          ? filters.safetyEventIdentification
          : null;
    const identification = normalizeSafetyEnum(
        identificationInput,
        SafetyEventId,
    ) ?? null;
    const types = (nestedSafetyEvent?.types ?? filters.safetyEventTypes ?? [])
        .map((type) => normalizeSafetyEnum(type, SafetyEventType))
        .filter((type): type is SafetyEventType => type !== undefined);
    if (filters.adverseEvent !== null && filters.adverseEvent !== undefined) {
        const adverseEventType = filters.adverseEvent
            ? SafetyEventType.AdverseEvent
            : SafetyEventType.NotAdverseEvent;
        if (!types.includes(adverseEventType)) {
            types.push(adverseEventType);
        }
    }
    const acknowledgments = (
        nestedSafetyEvent?.acknowledgments ??
        filters.safetyEventAcknowledgments ??
        []
    )
        .map((ack) => normalizeSafetyEnum(ack, SafetyEventAck))
        .filter((ack): ack is SafetyEventAck => ack !== undefined);

    if (identification !== null || types.length > 0 || acknowledgments.length > 0) {
        result.safetyEvent = {
            identification,
            typeOperator: normalizeSafetyEnum(
                nestedSafetyEvent?.typeOperator ?? filters.safetyEventTypeOperator,
                SafetyEventTypeOperator,
            ) ?? (nestedSafetyEvent ? SafetyEventTypeOperator.And : SafetyEventTypeOperator.Or),
            types,
            acknowledgments,
        };
    }

    if (filters.moduleScores && filters.moduleScores.length > 0) {
        result.moduleScores = filters.moduleScores
            .filter((ms) => ms.moduleId)
            .flatMap((ms): ModuleScore[] => {
                if (ms.operator === "Between" && ms.upperScore !== undefined) {
                    return [
                        {
                            licensedModuleId: ms.moduleId!,
                            operator: Operator.GtEq,
                            moduleScore: ms.score,
                        },
                        {
                            licensedModuleId: ms.moduleId!,
                            operator: Operator.LtEq,
                            moduleScore: ms.upperScore,
                        },
                    ];
                }

                return [
                    {
                        licensedModuleId: ms.moduleId!,
                        operator: convertModuleScoreOperator(ms.operator),
                        moduleScore: ms.score,
                    },
                ];
            });
    }

    const includeGroups = (filters.outlineSegmentNames ?? []).filter(
        (group) => group.length > 0,
    );
    const excludeGroups = (filters.excludeOutlineSegmentNames ?? []).filter(
        (group) => group.length > 0,
    );

    if (includeGroups.length > 0) {
        result.outlineSegmentFilterItems = includeGroups.flatMap(
            (group, filterGroupId): IOutlineFilter[] => [
                { value: true, segments: group, filterGroupId },
                ...excludeGroups.map(
                    (excludeGroup): IOutlineFilter => ({
                        value: false,
                        segments: excludeGroup,
                        filterGroupId,
                    }),
                ),
            ],
        );
    } else if (excludeGroups.length > 0) {
        result.outlineSegmentFilterItems = excludeGroups.map(
            (group): IOutlineFilter => ({
                value: false,
                segments: group,
                filterGroupId: 0,
            }),
        );
    }

    return result;
}

function normalizeSafetyEnum<T extends number>(
    value: T | string | null | undefined,
    enumValues: Record<string, string | number>,
): T | undefined {
    if (typeof value === "number") {
        return Object.values(enumValues).includes(value) ? value : undefined;
    }

    const parsed = value === null || value === undefined
        ? undefined
        : enumValues[value];
    return typeof parsed === "number" ? parsed as T : undefined;
}

function getDateReferenceOption(
    dateReference: string | undefined,
): DateReferenceOption {
    if (
        dateReference &&
        Object.values(DateReferenceOption).includes(
            dateReference as DateReferenceOption,
        )
    ) {
        return dateReference as DateReferenceOption;
    }

    return DateReferenceOption.InteractionDate;
}

function convertModuleScoreOperator(
    kboOperator: string,
): Operator | undefined {
    switch (kboOperator) {
        case "GreaterThan":
            return Operator.Gt;
        case "GreaterThanOrEqualTo":
            return Operator.GtEq;
        case "LessThan":
            return Operator.Lt;
        case "LessThanOrEqualTo":
            return Operator.LtEq;
        case "NotEqualTo":
            return Operator.NEq;
        case "EqualTo":
            return Operator.Eq;
        case "Between":
            return Operator.Between;
        default:
            return undefined;
    }
}

export const useOpenCloseFullscreenAgent = () => {
    const agentStore = useStore(AgentStore);
    const navigate = useNavigate();

    const closeFullscreenAgent = () => {
        agentStore.setFullscreen(false);
        agentStore.openDrawer();
        navigate(agentStore.routeBeforeFullscreen);
    };

    const openFullscreenAgent = () => {
        const currentPath = window.location.pathname;
        agentStore.setRouteBeforeFullscreen(currentPath);
        navigate(
            "/app/kbo/chats" +
                (agentStore.currentChatId !== emptyGUID
                    ? `/${agentStore.currentChatId}`
                    : ""),
        );
        agentStore.setFullscreen(true);
        agentStore.closeDrawer();
    };

    return { openFullscreenAgent, closeFullscreenAgent };
};
